// Access report engine: renders MS Access report layouts (layouts.json, converted from the
// Spa Buddy export) to printable HTML at their original cm positions, fonts and formats.
import LAYOUTS from "./layouts.json";

const KW = new Set(["report", "form", "reports", "forms"]);
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const isDate = (v) => v instanceof Date && !isNaN(v);
const num = (v) => (isDate(v) ? v.getTime() / 86400000 : typeof v === "number" ? v : v === "" || v == null ? 0 : Number(String(v).replace(/[$,]/g, "")) || 0);
const ERR = { err: true };

/* ---------- expression tokenizer / parser ---------- */
function tokens(src) {
  const out = []; let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) { i++; continue; }
    if (ch === '"') { let s = ""; i++; while (i < src.length) { if (src[i] === '"') { if (src[i + 1] === '"') { s += '"'; i += 2; continue; } break; } s += src[i++]; } i++; out.push({ t: "s", v: s }); continue; }
    if (ch === "[") { let j = src.indexOf("]", i); if (j < 0) j = src.length; out.push({ t: "id", v: src.slice(i + 1, j) }); i = j + 1; continue; }
    if (/[0-9]/.test(ch) || (ch === "." && /[0-9]/.test(src[i + 1] || ""))) { let j = i; while (j < src.length && /[0-9.]/.test(src[j])) j++; out.push({ t: "n", v: parseFloat(src.slice(i, j)) }); i = j; continue; }
    if (/[A-Za-z_]/.test(ch)) { let j = i; while (j < src.length && /\w/.test(src[j])) j++; out.push({ t: "w", v: src.slice(i, j) }); i = j; continue; }
    const two = src.slice(i, i + 2);
    if (["<>", "<=", ">="].includes(two)) { out.push({ t: "o", v: two }); i += 2; continue; }
    out.push({ t: "o", v: ch }); i++;
  }
  return out;
}

function parse(src) {
  const tk = tokens(src); let p = 0;
  const peek = () => tk[p]; const next = () => tk[p++];
  const isOp = (v) => peek() && peek().t === "o" && peek().v === v;
  const isW = (v) => peek() && peek().t === "w" && peek().v.toLowerCase() === v;
  const bin = (sub, ops, kw) => () => { let l = sub(); for (;;) { const k = peek(); if (!k) return l; const op = kw ? (k.t === "w" ? k.v.toLowerCase() : null) : (k.t === "o" ? k.v : null); if (!ops.includes(op)) return l; p++; l = { op, l, r: sub() }; } };
  const primary = () => {
    const k = next();
    if (!k) return { v: "" };
    if (k.t === "s" || k.t === "n") return { v: k.v };
    if (k.t === "o" && k.v === "(") { const e = orE(); next(); return e; }
    if (k.t === "o" && k.v === "-") return { op: "neg", l: primary() };
    if (k.t === "w" && k.v.toLowerCase() === "not") return { op: "not", l: cmp() };
    if (k.t === "w" && isOp("(")) { next(); const args = []; while (!isOp(")")) { args.push(orE()); if (isOp(",")) next(); else break; } next(); return { fn: k.v.toLowerCase(), args }; }
    const path = [k.v];
    while (isOp("!") || isOp(".")) { next(); const n = next(); if (n) path.push(n.v); }
    if (isOp("(")) { next(); const col = peek() && peek().t === "n" ? peek().v : 0; while (!isOp(")") && peek()) next(); next(); return { ref: [col === 2 ? "State" : col === 1 ? "Postcode" : "__none"] }; } // [Suburb].Column(n)
    if (k.t === "w" && path.length === 1 && ["date", "now", "time", "true", "false", "null"].includes(k.v.toLowerCase())) return { fn: k.v.toLowerCase(), args: [] };
    return { ref: path.filter((s, i) => !(KW.has(String(s).toLowerCase()) && i < path.length - 1)) };
  };
  const mul = bin(primary, ["*", "/"]);
  const add = bin(mul, ["+", "-"]);
  const cat = bin(add, ["&"]);
  const cmp = bin(cat, ["=", "<>", "<", ">", "<=", ">="]);
  const andE = bin(cmp, ["and"], true);
  const orE = bin(andE, ["or"], true);
  const e = orE();
  void isW;
  return e;
}
const _cache = {};
const compiled = (src) => (_cache[src] = _cache[src] || parse(src));

/* ---------- evaluation ---------- */
function evalNode(n, ctx) {
  if ("v" in n) return n.v;
  if (n.ref) return ctx.lookup(n.ref);
  if (n.fn) return callFn(n.fn, n.args, ctx);
  if (n.op === "neg") return -num(evalNode(n.l, ctx));
  if (n.op === "not") return !evalNode(n.l, ctx);
  const a = evalNode(n.l, ctx), b = evalNode(n.r, ctx);
  switch (n.op) {
    case "&": return `${a === ERR || a == null ? "" : fmtVal(a)}${b === ERR || b == null ? "" : fmtVal(b)}`;
    case "+": return isDate(a) ? new Date(a.getTime() + num(b) * 86400000) : (typeof a === "string" && typeof b === "string" && isNaN(a) ? a + b : num(a) + num(b));
    case "-": return isDate(a) && !isDate(b) ? new Date(a.getTime() - num(b) * 86400000) : num(a) - num(b);
    case "*": return num(a) * num(b);
    case "/": return num(b) ? num(a) / num(b) : ERR;
    case "=": return looseEq(a, b);
    case "<>": return !looseEq(a, b);
    case "<": return num(a) < num(b);
    case ">": return num(a) > num(b);
    case "<=": return num(a) <= num(b);
    case ">=": return num(a) >= num(b);
    case "and": return !!a && !!b;
    case "or": return !!a || !!b;
    default: return "";
  }
}
const looseEq = (a, b) => (typeof a === "number" || typeof b === "number" ? Math.abs(num(a) - num(b)) < 0.005 : String(a ?? "") === String(b ?? ""));

function callFn(fn, args, ctx) {
  const v = (i) => evalNode(args[i], ctx);
  switch (fn) {
    case "iif": return v(0) && v(0) !== ERR ? v(1) : v(2);
    case "isnull": { const x = v(0); return x == null || x === ""; }
    case "iserror": { const x = v(0); return x === ERR || x === undefined; }
    case "nz": { const x = v(0); return x == null || x === "" ? (args[1] ? v(1) : 0) : x; }
    case "sum": return ctx.rows.reduce((s, r) => s + num(evalNode(args[0], ctx.child(r))), 0);
    case "count": return ctx.rows.length;
    case "avg": return ctx.rows.length ? callFn("sum", args, ctx) / ctx.rows.length : 0;
    case "date": { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
    case "now": return new Date();
    case "time": return fmtTime(new Date());
    case "true": return true;
    case "false": return false;
    case "null": return null;
    case "format": return fmtVal(v(0), args[1] ? String(v(1)) : "");
    case "ucase": return String(v(0) ?? "").toUpperCase();
    case "lcase": return String(v(0) ?? "").toLowerCase();
    case "trim": return String(v(0) ?? "").trim();
    case "left": return String(v(0) ?? "").slice(0, num(v(1)));
    case "right": { const s = String(v(0) ?? ""); return s.slice(Math.max(0, s.length - num(v(1)))); }
    case "len": return String(v(0) ?? "").length;
    case "abs": return Math.abs(num(v(0)));
    case "round": return Number(num(v(0)).toFixed(args[1] ? num(v(1)) : 0));
    case "cdbl": case "val": case "ccur": case "cint": return num(v(0));
    case "year": return isDate(v(0)) ? v(0).getFullYear() : "";
    default: return "";
  }
}

/* ---------- formatting ---------- */
const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (d) => `${d.getHours() % 12 || 12}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${d.getHours() < 12 ? "AM" : "PM"}`;
const shortDate = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
const money = (n, neg = "-$") => `${n < 0 ? neg : "$"}${Math.abs(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export function fmtVal(v, fmt = "", dp) {
  if (v === ERR) return "#Error";
  if (v == null) return "";
  const f = String(fmt || "");
  if (isDate(v)) {
    if (/long date/i.test(f)) return v.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (/medium date/i.test(f)) return v.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" }).replace(/ /g, "-");
    if (/time/i.test(f)) return fmtTime(v);
    const hasT = v.getHours() || v.getMinutes() || v.getSeconds();
    return hasT && !/short date/i.test(f) ? `${shortDate(v)} ${fmtTime(v)}` : shortDate(v);
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (f === ">") return String(v).toUpperCase();
  if (typeof v === "number" || (f && /\$|currency|fixed|standard|percent|0/i.test(f) && v !== "" && !isNaN(Number(v)))) {
    const n = num(v);
    if (/\$|currency/i.test(f)) return money(n);
    if (/fixed/i.test(f)) return n.toFixed(dp ?? 2);
    if (/standard/i.test(f)) return n.toLocaleString("en-AU", { minimumFractionDigits: dp ?? 2, maximumFractionDigits: dp ?? 2 });
    if (/percent/i.test(f)) return `${(n * 100).toFixed(dp ?? 2)}%`;
    if (dp != null && dp !== 255) return n.toFixed(dp);
    return String(Math.round(n * 1e6) / 1e6);
  }
  return String(v);
}

/* ---------- data scope ---------- */
// ds = { rows: [..], refs: {}, subs: { ReportOrControlName: ds } }
function makeCtx(rep, ds, row, globals) {
  const named = {};
  rep.sections.forEach((s) => s.controls.forEach((c) => { if (c.name && c.src && c.t !== "Label" && c.t !== "Subform") named[c.name] = c; }));
  const rows = ds.rows || [];
  const stack = globals.__stack || (globals.__stack = new Set());
  const ctx = {
    rows, row, globals, depth: 0,
    child: (r) => makeCtx(rep, ds, r, globals),
    lookup(path) {
      if (path.length === 1) {
        const k = path[0];
        if (row && k in row) return row[k];
        if (named[k] && named[k].src !== k && !stack.has(k)) { stack.add(k); try { return ctx.value(named[k]); } finally { stack.delete(k); } }
        if (k in (globals.fields || {})) return globals.fields[k];
        return row ? undefined : ERR;
      }
      let o = { ...(globals.refs || {}), ...(ds.refs || {}) };
      for (const s of path) { if (o == null) break; o = o[s]; }
      return o === undefined ? ERR : o;
    },
    value(c) {
      if (ctx.depth > 30) return ERR;
      ctx.depth++;
      try { return c.src.startsWith("=") ? evalNode(compiled(c.src.slice(1)), ctx) : ctx.lookup([c.src]); }
      catch { return ERR; } finally { ctx.depth--; }
    },
  };
  return ctx;
}

// Summary values of a sub-report (named header/footer controls) so parents can read [Sub].[Report]![X].
function summarise(name, ds, globals) {
  const rep = LAYOUTS[name]; if (!rep) return {};
  const ctx = makeCtx(rep, ds, ds.rows?.[0], globals);
  const out = { ...(ds.rows?.[0] || {}) };
  if (!ds.rows?.length) Object.keys(out).forEach((k) => delete out[k]);
  rep.sections.forEach((s) => s.controls.forEach((c) => {
    if (!(c.name && c.src && c.t === "TextBox")) return;
    if (s.kind === "Section" && c.src.startsWith("=") && !/sum\(|count\(/i.test(c.src) && (ds.rows || []).length > 1) {
      const vals = ds.rows.map((r) => ctx.child(r).value(c));
      if (vals.every((x) => typeof x === "number")) { out[c.name] = vals.reduce((a, b) => a + b, 0); return; }
    }
    const v = ctx.value(c); if (v !== ERR && (ds.rows?.length || s.kind !== "Section")) out[c.name] = v;
  }));
  return out;
}

/* ---------- rendering ---------- */
const ALIGN = { 1: "left", 2: "center", 3: "right", 4: "justify" };
const PT = 0.03528; // cm per point

function childDs(c, ds, globals) {
  const src = String(c.src || "").replace(/^(Report|Form)\./, "");
  const key = c.src.startsWith("Form.") ? `Form_${src}` : src;
  const sub = (ds.subs || {})[key] || (ds.subs || {})[c.name] || (globals.subs || {})[key];
  const co = /company|logo|logon|banking/i.test(key);
  const base = co ? { ...(ds.rows?.[0] || {}), ...(globals.company || {}) } : { ...(globals.company || {}), ...(ds.rows?.[0] || {}) };
  return { key, ds: sub || { rows: [base], subs: ds.subs, refs: ds.refs } };
}

function textGrowH(text, c) {
  const fs = c.fs || 11;
  const perLine = Math.max(1, Math.floor(c.w / (fs * PT * 0.5)));
  const lines = String(text).split("\n").reduce((n, l) => n + Math.max(1, Math.ceil(l.length / perLine)), 0);
  return lines * fs * PT * 1.2 + 0.1;
}

function renderSection(rep, sec, ds, row, globals) {
  const ctx = makeCtx(rep, ds, row, globals);
  const parts = [];
  const ctrls = sec.controls.map((c, z) => ({ ...c, z })).filter((c) => !c.hide && (c.w > 0.01 || c.t === "Line")).sort((a, b) => a.y - b.y);
  const bands = []; // grow bands {top, from, d}; side-by-side growth counts once (max)
  const grow = (top, from, d) => {
    if (d <= 0.01) return;
    const b = bands.find((x) => top < x.from - 0.01 && x.top < from - 0.01);
    if (b) { b.d = Math.max(b.d, d); b.from = Math.max(b.from, from); } else bands.push({ top, from, d });
  };
  const shiftOf = (y) => bands.reduce((s, b) => (y >= b.from - 0.01 ? s + b.d : s), 0);
  const shifts = bands;
  let bottom = sec.h;
  for (const c of ctrls) {
    const top = c.y + shiftOf(c.y);
    const box = `left:${c.l}cm;top:${top}cm;width:${c.w}cm;z-index:${c.z};`;
    if (c.t === "Subform") {
      const { key, ds: cds } = childDs(c, ds, globals);
      if (!LAYOUTS[key]) continue;
      const { html, h } = renderBody(key, cds, globals, true);
      const hh = Math.max(h, 0);
      parts.push(`<div style="position:absolute;${box}height:${hh}cm;overflow:visible">${html}</div>`);
      grow(c.y, c.y + c.h, hh - c.h);
      bottom = Math.max(bottom, top + hh);
      continue;
    }
    if (c.t === "Line") {
      const st = c.h < 0.02 ? `width:${c.w}cm;height:0;border-top:${c.bw || 1}px solid ${c.bd || "#000"}` : `width:0;height:${c.h}cm;border-left:${c.bw || 1}px solid ${c.bd || "#000"}`;
      parts.push(`<div style="position:absolute;left:${c.l}cm;top:${top}cm;${st}"></div>`);
      continue;
    }
    if (c.t === "Rectangle") {
      parts.push(`<div style="position:absolute;${box}height:${c.h}cm;${c.bd ? `border:${c.bw || 1}px solid ${c.bd};` : ""}${c.bg ? `background:${c.bg};` : ""}box-sizing:border-box"></div>`);
      continue;
    }
    if (c.t === "Image" || c.t === "BoundObjectFrame" || c.t === "UnboundObjectFrame") {
      const val = c.t === "Image" ? null : ctx.lookup([c.src || ""]);
      const url = (typeof val === "string" && /^(https?:|\/|data:)/.test(val) && val) || (globals.images || {})[c.src] || (/logo/i.test(c.src || c.name) ? globals.company?.logo_url : "");
      if (url) parts.push(`<img src="${esc(url)}" style="position:absolute;${box}height:${c.h}cm;object-fit:contain;object-position:left top;background:#fff"/>`);
      continue;
    }
    if (c.t === "CheckBox") { const v = ctx.lookup([c.src || ""]); parts.push(`<div style="position:absolute;${box}font-size:10pt">${v && v !== ERR ? "&#9745;" : "&#9744;"}</div>`); continue; }
    let text, raw;
    const ov = (globals.text || {})[`${rep.name}.${c.name}`];
    if (ov != null) text = ov;
    else if (c.t === "Label") text = c.src || "";
    else { const v = c.src ? ctx.value(c) : ""; raw = v; text = v === ERR ? "" : fmtVal(v, c.fmt, c.dp); if (c.fmt === ">") text = text.toUpperCase(); }
    let h = c.h > 0.05 ? c.h : (c.fs || 11) * PT * 1.25;
    if ((c.grow || (ov != null && ov.length > String(c.src || "").length + 2)) && text) { const gh = textGrowH(text, c); if (gh > h) { grow(c.y, c.y + c.h, gh - h); h = gh; } }
    if (!text && !c.bg && !c.bd) continue;
    const isNum = c.t === "TextBox" && (typeof raw === "number" || /\$/.test(text)) && /^-?\$?[\d,.]+%?$/.test(text);
    const st = `font-family:'${c.font || "Calibri"}',Calibri,Carlito,Arial,sans-serif;font-size:${c.fs || 11}pt;${c.b ? "font-weight:700;" : ""}${c.i ? "font-style:italic;" : ""}${c.u ? "text-decoration:underline;" : ""}color:${c.fg || "#000"};${c.bg ? `background:${c.bg};` : ""}text-align:${ALIGN[c.al] || (isNum ? "right" : "left")};${c.bd ? `border:1px solid ${c.bd};` : ""}`;
    // single-line boxes clip sideways instead of wrapping a half-visible 2nd line over the control below
    const oneLine = !c.grow && !String(text).includes("\n") && h < (c.fs || 11) * PT * 2.3;
    const flow = oneLine ? `overflow:${c.t === "Label" || (isNum && c.al === 2) ? "visible" : "hidden"};white-space:pre;` : "overflow:hidden;white-space:pre-wrap;";
    // script wording is always meant to be read, so it sits above overlapping logos
    parts.push(`<div style="position:absolute;${ov != null ? box.replace(`z-index:${c.z}`, "z-index:1000") : box}${c.grow ? "min-" : ""}height:${h}cm;${st}${flow}line-height:1.2;padding:0 0.04cm;box-sizing:border-box">${esc(text)}</div>`);
    bottom = Math.max(bottom, top + h);
  }
  bottom = Math.max(bottom, sec.h + shifts.reduce((t, b) => t + b.d, 0));
  return { html: `<div style="position:relative;height:${bottom}cm;${sec.bg && sec.bg !== "#ffffff" ? `background:${sec.bg};` : ""}">${parts.join("")}</div>`, h: bottom };
}

const ORDER = ["FormHeader", "PageHeader", "BreakHeader", "Section", "BreakFooter", "FormFooter"];
function renderBody(name, ds, globals, isSub) {
  const rep = LAYOUTS[name];
  ds = { ...ds, refs: { ...(ds.refs || {}) } };
  // pre-compute every sub-report's summary so [Sub].[Report]![Field] references resolve
  rep.sections.forEach((s) => s.controls.forEach((c) => {
    if (c.t !== "Subform" || !c.src) return;
    const { key, ds: cds } = childDs(c, ds, globals);
    ds.refs[c.name] = summarise(key, cds, globals);
  }));
  const rows = ds.rows || [];
  if (isSub && !rows.length) return { html: "", h: 0, width: rep.width };
  let html = "", h = 0;
  for (const kind of ORDER) {
    if (isSub && kind === "PageHeader") continue;
    rep.sections.filter((s) => s.kind === kind && s.visible && (s.h > 0 || s.controls.length)).forEach((sec) => {
      const list = kind === "Section" ? rows : [rows[0]];
      list.forEach((r) => { const out = renderSection(rep, sec, ds, r, globals); html += out.html; h += out.h; });
    });
  }
  const pf = rep.sections.find((s) => s.kind === "PageFooter" && s.visible && s.controls.length);
  const footer = !isSub && pf ? renderSection(rep, pf, ds, rows[0], globals) : null;
  return { html, h, footer, width: rep.width };
}

/** Full printable HTML document for an Access report. paper: "a4" | "receipt" */
export function reportHtml(name, ds, globals = {}, { paper = "a4", title = name } = {}) {
  const { html, footer, width } = renderBody(name, ds, globals, false);
  const receipt = paper === "receipt";
  const page = receipt
    ? `<div style="width:${width}cm;margin:0 auto">${html}${footer ? footer.html : ""}</div>`
    : `<div class="pg" style="width:${width}cm;min-height:27.6cm;position:relative;margin:0 auto;padding-bottom:${footer ? footer.h : 0}cm;box-sizing:border-box">${html}${footer ? `<div style="position:absolute;left:0;bottom:0;width:100%">${footer.html}</div>` : ""}</div>`;
  return `<!doctype html><html><head><meta charset="utf-8"/><title>${esc(title)}</title><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Carlito:ital,wght@0,400;0,700;1,400;1,700&display=swap"/><style>
    @page { size: ${receipt ? "80mm auto" : "A4"}; margin: ${receipt ? "2mm 4mm" : "1cm 0.7cm"}; }
    html,body{margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    @media screen { body{padding:16px;background:#e5e7eb} .pg,body>div{background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.2)} }
  </style></head><body>${page}<script>window.onload=function(){(document.fonts?document.fonts.ready:Promise.resolve()).then(function(){setTimeout(function(){window.print()},250)})}</script></body></html>`;
}

export const hasLayout = (n) => !!LAYOUTS[n];
